import React, { createContext, useState, useContext, useEffect } from 'react';
import { initialPosts } from './mockData';

const ArticleContext = createContext();

export const ArticleProvider = ({ children }) => {
  // 从 localStorage 读取文章数据，如果没有则使用初始数据
  const getInitialArticles = () => {
    const savedArticles = localStorage.getItem('blog_articles');
    if (savedArticles) {
      try {
        return JSON.parse(savedArticles);
      } catch (error) {
        console.error('解析文章数据失败:', error);
        return getDefaultArticles();
      }
    }
    return getDefaultArticles();
  };

  // 默认文章数据（使用mock数据）
  const getDefaultArticles = () => {
    return initialPosts.map(post => ({
      id: parseInt(post.id) || Date.now(),
      title: post.title,
      excerpt: post.summary,
      date: post.date.split(' ')[0], // 只取日期部分
      author: post.author,
      readTime: post.readTime,
      tags: post.tags,
      category: post.category,
      content: post.content,
      views: post.views || 0,
      likes: post.likes || 0,
      comments: post.comments || 0
    }));
  };

  const [articles, setArticles] = useState(getInitialArticles());

  // 当文章数据变化时，保存到 localStorage
  useEffect(() => {
    localStorage.setItem('blog_articles', JSON.stringify(articles));
  }, [articles]);

  // 获取下一个可用的文章ID
  const getNextId = () => {
    if (articles.length === 0) return 1;
    const maxId = Math.max(...articles.map(article => article.id));
    return maxId + 1;
  };

  // 发布新文章
  const publishArticle = (articleData) => {
    return new Promise((resolve) => {
      const newArticle = {
        id: getNextId(),
        ...articleData,
        date: new Date().toISOString().split('T')[0], // 当前日期
        author: articleData.author || 'zxj',
        readTime: calculateReadTime(articleData.content),
        tags: articleData.tags ? articleData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : ['未分类'],
        content: articleData.content || '',
        excerpt: articleData.excerpt || ''
      };
      
      setArticles(prevArticles => [newArticle, ...prevArticles]);
      
      setTimeout(() => {
        resolve(newArticle);
      }, 500);
    });
  };

  // 计算阅读时间
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes}分钟`;
  };

  // 删除文章
  const deleteArticle = (id) => {
    return new Promise((resolve) => {
      setArticles(prevArticles => prevArticles.filter(article => article.id !== id));
      
      setTimeout(() => {
        resolve(true);
      }, 500);
    });
  };

  // 更新文章
  const updateArticle = (id, updatedData) => {
    return new Promise((resolve) => {
      setArticles(prevArticles => 
        prevArticles.map(article => 
          article.id === id ? { ...article, ...updatedData } : article
        )
      );
      
      setTimeout(() => {
        resolve(true);
      }, 500);
    });
  };

  // 获取所有文章
  const getAllArticles = () => {
    return articles;
  };

  // 按年份分组
  const getArticlesByYear = () => {
    const grouped = {};
    articles.forEach(article => {
      const year = article.date.split('-')[0];
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(article);
    });
    return grouped;
  };

  // 获取文章详情
  const getArticleById = (id) => {
    return articles.find(article => article.id === parseInt(id));
  };

  // 获取分页文章
  const getPaginatedArticles = (page = 1, pageSize = 10) => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return articles.slice(start, end);
  };

  // 获取文章总数
  const getTotalArticlesCount = () => {
    return articles.length;
  };

  return (
    <ArticleContext.Provider value={{
      articles,
      publishArticle,
      deleteArticle,
      updateArticle,
      getAllArticles,
      getArticlesByYear,
      getArticleById,
      getPaginatedArticles,
      getTotalArticlesCount,
      totalArticles: articles.length
    }}>
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticles must be used within ArticleProvider');
  }
  return context;
};